import React, { useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/free-solid-svg-icons';
import { FormGroup, Input, ModalBody, ModalHeader } from 'reactstrap';
import Rodal from '../../Rodal/Rodal';
import ButtonLoader from '../../Layout/Buttons/ButtonLoader';
import './UploadTransactionDocumentModal.scss';

interface UploadTransactionDocumentModalProps {
    isOpen: boolean;
    requiredDocumentTypes: string[];
    onClose: () => void;
    onUpload: (
        documentType: string,
        file: File,
        comment: string
    ) => Promise<boolean>;
}

export const UploadTransactionDocumentModal = (
    props: UploadTransactionDocumentModalProps
) => {
    const [documentType, setDocumentType] = useState('');
    const [file, setFile] = useState<File | null>(null);
    const [comment, setComment] = useState('');
    const [isUploading, setIsUploading] = useState(false);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        multiple: false,
        onDrop: (acceptedFiles: File[]) => {
            if (acceptedFiles.length > 0) {
                setFile(acceptedFiles[0]);
            }
        },
    });

    useEffect(() => {
        if (props.isOpen) {
            setDocumentType(props.requiredDocumentTypes[0] ?? '');
            setFile(null);
            setComment('');
        }
    }, [props.isOpen]);

    const upload = async () => {
        setIsUploading(true);
        const succeeded = await props.onUpload(documentType, file, comment.trim());
        setIsUploading(false);
        if (succeeded) {
            props.onClose();
        }
    };

    return (
        <Rodal
            visible={props.isOpen}
            onClose={props.onClose}
            animation="fade"
            showMask={true}
            width={500}
            showCloseButton={true}
            className="upload-transaction-document-modal">
            <ModalHeader>Upload Document</ModalHeader>
            <ModalBody>
                <FormGroup>
                    <label
                        className="upload-transaction-document__label"
                        htmlFor="upload-transaction-document-type">
                        Document Type *
                    </label>
                    <Input
                        type="select"
                        id="upload-transaction-document-type"
                        value={documentType}
                        onChange={(e: any) => setDocumentType(e.target.value)}>
                        {props.requiredDocumentTypes.map((type) => (
                            <option value={type} key={type}>
                                {type}
                            </option>
                        ))}
                    </Input>
                </FormGroup>
                <FormGroup>
                    <label className="upload-transaction-document__label">
                        File
                    </label>
                    <div
                        {...getRootProps({
                            className: isDragActive
                                ? 'upload-transaction-document__dropzone upload-transaction-document__dropzone--active'
                                : 'upload-transaction-document__dropzone',
                        })}>
                        <input {...getInputProps()} />
                        <FontAwesomeIcon
                            icon={faCloudArrowUp}
                            className="upload-transaction-document__dropzone-icon"
                        />
                        {file ? (
                            <p className="upload-transaction-document__dropzone-label">
                                {file.name}
                            </p>
                        ) : (
                            <p className="upload-transaction-document__dropzone-label">
                                Drag a file here or{' '}
                                <span className="upload-transaction-document__browse">
                                    browse
                                </span>
                            </p>
                        )}
                    </div>
                </FormGroup>
                <FormGroup>
                    <label
                        className="upload-transaction-document__label"
                        htmlFor="upload-transaction-document-comment">
                        Comments
                    </label>
                    <Input
                        type="textarea"
                        id="upload-transaction-document-comment"
                        rows={4}
                        maxLength={1000}
                        value={comment}
                        onChange={(e: any) => setComment(e.target.value)}
                        placeholder="What changed in this version?"
                    />
                </FormGroup>
                <div className="buttons-wrapper">
                    <button
                        type="button"
                        className="btn btn-cancel"
                        onClick={props.onClose}>
                        Cancel
                    </button>
                    <ButtonLoader
                        className="btn btn-primary"
                        buttonText="Upload"
                        loaderButtonText="Upload"
                        isLoading={isUploading}
                        disabled={!documentType || !file}
                        onClick={upload}
                    />
                </div>
            </ModalBody>
        </Rodal>
    );
};
